// src/pages/league-dashboard/live-draft/components/TradeProposalModal.js
// Modal for proposing trades during live draft
import React, { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, ArrowLeftRight, Check, AlertCircle } from 'lucide-react';
import { dynastyTheme } from '../../../../services/colorService';
import { leaguesAPI } from '../../../../services/apiService';

// Pure trade-payload assembler — extracted from handleSubmit per Bible
// §14 frontend testing convention so unit tests can target the canonical
// trade-request shape (including offering/requesting player metadata
// resolution from the my/their roster arrays) without exercising the
// full modal render flow. Returns the request object passed to
// onProposeTrade.
export const buildTradePayload = ({
  selectedTeam,
  offeringPicks,
  requestingPicks,
  offeringPlayers,
  requestingPlayers,
  myPlayers,
  allTheirPlayers,
}) => ({
  receiving_team_id: selectedTeam.team_id,
  offering_picks: offeringPicks,
  requesting_picks: requestingPicks,
  offering_players: offeringPlayers.map(id => {
    const player = myPlayers.find(p => p.ids?.league_player === id);
    return {
      id,
      full_name: player?.info?.full_name || 'Unknown Player',
      position: player?.info?.position,
      salary: player?.league_context?.financial?.contract_salary
    };
  }),
  requesting_players: requestingPlayers.map(id => {
    const player = allTheirPlayers.find(p => p.ids?.league_player === id);
    return {
      id,
      full_name: player?.info?.full_name || 'Unknown Player',
      position: player?.info?.position,
      salary: player?.league_context?.financial?.contract_salary
    };
  }),
});

export const TradeProposalModal = React.memo(({
  isOpen,
  onClose,
  myTeam,
  myPicks,
  myPlayers,
  allTeams,
  allPicks,
  onProposeTrade,
  isProposing = false,
  leagueId
}) => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [offeringPicks, setOfferingPicks] = useState([]);
  const [requestingPicks, setRequestingPicks] = useState([]);
  const [offeringPlayers, setOfferingPlayers] = useState([]);
  const [requestingPlayers, setRequestingPlayers] = useState([]);
  const [theirPlayers, setTheirPlayers] = useState([]);

  // Fetch the selected team's roster when selection changes
  useEffect(() => {
    if (!selectedTeam || !leagueId) {
      setTheirPlayers([]);
      return;
    }
    const fetchTheirRoster = async () => {
      try {
        const response = await leaguesAPI.getTeamRosterCanonical(leagueId, selectedTeam.team_id);
        setTheirPlayers(response?.players || []);
      } catch (err) {
        console.error('[TradeProposalModal] Error fetching team roster for trade:', err);
        setTheirPlayers([]);
      }
    };
    fetchTheirRoster();
  }, [selectedTeam, leagueId]);

  // Players the other team has drafted this session (from allPicks)
  const theirDraftedPlayers = useMemo(() => {
    if (!selectedTeam || !allPicks) return [];
    return allPicks
      .filter(p =>
        p.current_owner_team_id === selectedTeam.team_id &&
        (p.player_selected || p.league_player_id)
      )
      .map(p => p.player_selected || {
        ids: { league_player: p.league_player_id },
        info: {
          full_name: p.player?.info?.full_name,
          position: p.player?.info?.position,
          mlb_team: p.player?.info?.mlb_team
        }
      });
  }, [selectedTeam, allPicks]);

  // Combine roster players with session-drafted players (excluding any already in roster)
  const allTheirPlayers = useMemo(() => {
    const rosterIds = new Set(theirPlayers.map(p => p.ids?.league_player).filter(Boolean));
    const newDrafted = theirDraftedPlayers.filter(p => !rosterIds.has(p.ids?.league_player));
    return [...theirPlayers, ...newDrafted];
  }, [theirPlayers, theirDraftedPlayers]);

  // Get available picks for selected team (unused picks only)
  const theirAvailablePicks = useMemo(() => {
    if (!selectedTeam) return [];
    return allPicks.filter(p =>
      p.current_owner_team_id === selectedTeam.team_id &&
      !p.league_player_id &&
      !p.player_selected
    ).sort((a, b) => a.overall_pick_number - b.overall_pick_number);
  }, [selectedTeam, allPicks]);

  // My available picks (unused only)
  const myAvailablePicks = useMemo(() => {
    if (!myPicks) return [];
    return myPicks.filter(p =>
      !p.league_player_id &&
      !p.player_selected
    ).sort((a, b) => a.overall_pick_number - b.overall_pick_number);
  }, [myPicks]);

  // Other teams (not my team)
  const otherTeams = useMemo(() => {
    if (!allTeams || !myTeam) return [];
    return allTeams.filter(t => t.team_id !== myTeam.team_id);
  }, [allTeams, myTeam]);

  // Toggle handlers — picks
  const toggleOfferingPick = (pickId) => {
    setOfferingPicks(prev =>
      prev.includes(pickId) ? prev.filter(id => id !== pickId) : [...prev, pickId]
    );
  };

  const toggleRequestingPick = (pickId) => {
    setRequestingPicks(prev =>
      prev.includes(pickId) ? prev.filter(id => id !== pickId) : [...prev, pickId]
    );
  };

  // Toggle handlers — players
  const toggleOfferingPlayer = (playerId) => {
    setOfferingPlayers(prev =>
      prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
    );
  };

  const toggleRequestingPlayer = (playerId) => {
    setRequestingPlayers(prev =>
      prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
    );
  };

  // Handle submit
  const handleSubmit = () => {
    if (!selectedTeam) return;
    const hasAssets = offeringPicks.length > 0 || requestingPicks.length > 0 ||
                      offeringPlayers.length > 0 || requestingPlayers.length > 0;
    if (!hasAssets) return;

    onProposeTrade(buildTradePayload({
      selectedTeam,
      offeringPicks,
      requestingPicks,
      offeringPlayers,
      requestingPlayers,
      myPlayers,
      allTheirPlayers,
    }));
  };

  // Reset state when modal closes
  const handleClose = () => {
    setSelectedTeam(null);
    setOfferingPicks([]);
    setRequestingPicks([]);
    setOfferingPlayers([]);
    setRequestingPlayers([]);
    setTheirPlayers([]);
    onClose();
  };

  // Validate trade
  const canSubmit = selectedTeam && (
    offeringPicks.length > 0 || requestingPicks.length > 0 ||
    offeringPlayers.length > 0 || requestingPlayers.length > 0
  );

  if (!isOpen) return null;

  const numTeams = allTeams?.length || 1;

  const renderPickList = (picks, selectedIds, onToggle, accentClass, checkboxAccent) => (
    picks.length === 0 ? null : (
      <div className="space-y-1 max-h-32 overflow-y-auto">
        {picks.map(pick => {
          const pickInRound = pick.pick_in_round ||
            (pick.overall_pick_number && numTeams > 0
              ? ((pick.overall_pick_number - 1) % numTeams) + 1
              : '?');
          return (
            <label
              key={pick.pick_id}
              className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors
                ${selectedIds.includes(pick.pick_id)
                  ? `${accentClass} border`
                  : 'hover:bg-gray-700/50'
                }`}
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(pick.pick_id)}
                onChange={() => onToggle(pick.pick_id)}
                className={`w-4 h-4 ${checkboxAccent}`}
              />
              <div className="flex-1">
                <span className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>
                  Round {pick.round}, Pick {pickInRound}
                </span>
                <span className={`text-xs ml-2 ${dynastyTheme.classes.text.neutralLight}`}>
                  (#{pick.overall_pick_number || 'TBD'})
                </span>
              </div>
            </label>
          );
        })}
      </div>
    )
  );

  const renderPlayerList = (players, selectedIds, onToggle, accentClass, checkboxAccent) => (
    players.length === 0 ? null : (
      <div className="space-y-1 max-h-32 overflow-y-auto">
        {players.map(player => {
          const playerId = player.ids?.league_player;
          if (!playerId) return null;
          const salary = player.league_context?.financial?.contract_salary;
          return (
            <label
              key={playerId}
              className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors
                ${selectedIds.includes(playerId)
                  ? `${accentClass} border`
                  : 'hover:bg-gray-700/50'
                }`}
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(playerId)}
                onChange={() => onToggle(playerId)}
                className={`w-4 h-4 ${checkboxAccent}`}
              />
              <div className="flex-1 min-w-0">
                <span className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>
                  {player.info?.full_name}
                </span>
                <span className={`text-xs ml-2 ${dynastyTheme.classes.text.neutralLight}`}>
                  {player.info?.position}
                  {salary != null ? ` — $${salary}` : ''}
                </span>
              </div>
            </label>
          );
        })}
      </div>
    )
  );

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <div
        className={`w-full max-w-2xl ${dynastyTheme.components.card.base} p-6 m-4 max-h-[90vh] overflow-y-auto`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ArrowLeftRight className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
            <h2 className={dynastyTheme.components.heading.h2}>Propose Trade</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Team selector */}
        <div className="mb-6">
          <label className={`block text-sm font-medium mb-2 ${dynastyTheme.classes.text.neutralLight}`}>
            Trade With:
          </label>
          <select
            value={selectedTeam?.team_id || ''}
            onChange={e => {
              const team = otherTeams.find(t => t.team_id === e.target.value);
              setSelectedTeam(team);
              setRequestingPicks([]);
              setRequestingPlayers([]);
            }}
            className={`w-full ${dynastyTheme.components.input}`}
          >
            <option value="">Select a team...</option>
            {otherTeams.map(team => (
              <option key={team.team_id} value={team.team_id}>
                {team.team_name}
              </option>
            ))}
          </select>
        </div>

        {selectedTeam && (
          <>
            {/* Trade grid */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* You Give */}
              <div className={`${dynastyTheme.components.card.base} p-4`}>
                <h3 className="text-sm font-bold mb-3 text-yellow-400 flex items-center gap-2">
                  <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                  You Give ({offeringPicks.length + offeringPlayers.length})
                </h3>

                {/* My picks */}
                {myAvailablePicks.length > 0 && (
                  <div className="mb-3">
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${dynastyTheme.classes.text.neutralLight}`}>
                      Draft Picks
                    </p>
                    {renderPickList(myAvailablePicks, offeringPicks, toggleOfferingPick,
                      'bg-yellow-500/20 border-yellow-500/50', 'accent-yellow-500')}
                  </div>
                )}

                {/* My players */}
                {myPlayers.length > 0 && (
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${dynastyTheme.classes.text.neutralLight}`}>
                      Players
                    </p>
                    {renderPlayerList(myPlayers, offeringPlayers, toggleOfferingPlayer,
                      'bg-yellow-500/20 border-yellow-500/50', 'accent-yellow-500')}
                  </div>
                )}

                {myAvailablePicks.length === 0 && myPlayers.length === 0 && (
                  <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                    No available assets to trade
                  </p>
                )}
              </div>

              {/* You Get */}
              <div className={`${dynastyTheme.components.card.base} p-4`}>
                <h3 className="text-sm font-bold mb-3 text-green-400 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                  You Get ({requestingPicks.length + requestingPlayers.length})
                </h3>

                {/* Their picks */}
                {theirAvailablePicks.length > 0 && (
                  <div className="mb-3">
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${dynastyTheme.classes.text.neutralLight}`}>
                      Draft Picks
                    </p>
                    {renderPickList(theirAvailablePicks, requestingPicks, toggleRequestingPick,
                      'bg-green-500/20 border-green-500/50', 'accent-green-500')}
                  </div>
                )}

                {/* Their players */}
                {allTheirPlayers.length > 0 && (
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${dynastyTheme.classes.text.neutralLight}`}>
                      Players
                    </p>
                    {renderPlayerList(allTheirPlayers, requestingPlayers, toggleRequestingPlayer,
                      'bg-green-500/20 border-green-500/50', 'accent-green-500')}
                  </div>
                )}

                {theirAvailablePicks.length === 0 && allTheirPlayers.length === 0 && (
                  <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                    No available assets from {selectedTeam.team_name}
                  </p>
                )}
              </div>
            </div>

            {/* Trade summary */}
            {canSubmit && (
              <div className={`${dynastyTheme.components.card.base} p-4 mb-6 border-l-4 border-yellow-500`}>
                <h4 className={`text-sm font-bold mb-2 ${dynastyTheme.classes.text.white}`}>
                  Trade Summary
                </h4>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex-1">
                    <span className={dynastyTheme.classes.text.neutralLight}>You give:</span>
                    <span className={`ml-2 ${dynastyTheme.classes.text.white}`}>
                      {[
                        offeringPicks.length > 0 && `${offeringPicks.length} pick(s)`,
                        offeringPlayers.length > 0 && `${offeringPlayers.length} player(s)`
                      ].filter(Boolean).join(', ') || 'Nothing'}
                    </span>
                  </div>
                  <ArrowLeftRight className="w-4 h-4 text-gray-500" />
                  <div className="flex-1 text-right">
                    <span className={dynastyTheme.classes.text.neutralLight}>You get:</span>
                    <span className={`ml-2 ${dynastyTheme.classes.text.white}`}>
                      {[
                        requestingPicks.length > 0 && `${requestingPicks.length} pick(s)`,
                        requestingPlayers.length > 0 && `${requestingPlayers.length} player(s)`
                      ].filter(Boolean).join(', ') || 'Nothing'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Warning if no team selected */}
        {!selectedTeam && (
          <div className={`${dynastyTheme.components.card.base} p-6 text-center`}>
            <AlertCircle className={`w-12 h-12 mx-auto mb-3 ${dynastyTheme.classes.text.neutralLight}`} />
            <p className={dynastyTheme.classes.text.neutralLight}>
              Select a team to start building your trade proposal
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-700">
          <button
            onClick={handleClose}
            className={`px-4 py-2 ${dynastyTheme.components.button.secondary}`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || isProposing}
            className={`px-6 py-2 rounded font-medium flex items-center gap-2 transition-colors
              ${canSubmit && !isProposing
                ? 'bg-yellow-500 hover:bg-yellow-400 text-black'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
          >
            {isProposing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent"></div>
                Sending...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Propose Trade
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
});

TradeProposalModal.displayName = 'TradeProposalModal';

export default TradeProposalModal;
